import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import { PhoneIcon, GlobeIcon, ShieldCheckIcon, LayoutDashboardIcon, UploadCloudIcon, FileTextIcon, CheckCircle2Icon, SearchIcon, TvIcon, CameraIcon, LockIcon, ZapIcon, TrendingDownIcon, SmileIcon, ClockIcon, FileXIcon } from 'lucide-react';
export function HeroSection({
  onOpenQuote
}: {
  onOpenQuote?: () => void;
}) {
  const [monthlySpend, setMonthlySpend] = useState(2500);
  const [selectedServices, setSelectedServices] = useState<string[]>(['Phone', 'Internet']);
  const [animationStep, setAnimationStep] = useState(0);
  // Savings Counter Logic
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    const savings = monthlySpend * 0.3 * 12;
    const controls = animate(count, savings, {
      duration: 1,
      ease: 'easeOut'
    });
    return controls.stop;
  }, [monthlySpend, count]);
  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  const toggleService = (service: string) => {
    setSelectedServices(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };
  return <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden bg-[#011B33]" data-id="element-136">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-137"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-transparent to-[#011B33]" data-id="element-138"></div>
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#22A39F]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-139"></div>
      <div className="absolute bottom-0 -left-24 w-96 h-96 bg-[#22A39F]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-140"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-141">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-142">
          {/* Left Side: Value Prop & Calculator */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-143">
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#22A39F]/10 border border-[#22A39F]/20 text-[#22A39F] text-xs font-bold mb-6" data-id="element-144">
              <span className="flex h-1.5 w-1.5 rounded-full bg-[#22A39F] mr-2 animate-pulse" data-id="element-145"></span>
              Telecom Expense Management 2.0
            </div>

            <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-[1.1] mb-4 tracking-tight" data-id="element-146">
              Stop Overpaying for <br data-id="element-147" />
              <span className="text-[#22A39F]" data-id="element-148">Business Technologies.</span>
            </h1>

            <p className="text-base text-slate-400 leading-relaxed mb-6 max-w-xl" data-id="element-149">
              Most businesses waste 30% of their budget on unused services and
              hidden fees. Our AI-driven audit uncovers the waste in minutes.
            </p>

            {/* Savings Calculator Card */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 mb-6 shadow-2xl" data-id="element-150">
              <div className="mb-6" data-id="element-151">
                <div className="flex justify-between items-center mb-3" data-id="element-152">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider" data-id="element-153">
                    Current Monthly Spend
                  </label>
                  <span className="text-xl font-bold text-white" data-id="element-154">
                    ${monthlySpend.toLocaleString()}
                  </span>
                </div>
                <input type="range" min="500" max="20000" step="100" value={monthlySpend} onChange={e => setMonthlySpend(parseInt(e.target.value))} className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-[#22A39F]" data-id="element-155" />
              </div>

              {/* Service Selection */}
              <div className="mb-6" data-id="element-156">
                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-3 block" data-id="element-157">
                  Select Services to Audit
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-id="element-158">
                  {[{
                  id: 'Phone',
                  icon: PhoneIcon
                }, {
                  id: 'Internet',
                  icon: GlobeIcon
                }, {
                  id: 'Website/CRM',
                  icon: LayoutDashboardIcon
                }, {
                  id: 'Camera',
                  icon: CameraIcon
                }, {
                  id: 'Access',
                  icon: LockIcon
                }].map(service => {
                  const isSelected = selectedServices.includes(service.id);
                  return <button key={service.id} onClick={() => toggleService(service.id)} className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all ${isSelected ? 'bg-[#22A39F] border-[#22A39F] text-white shadow-md shadow-[#22A39F]/20' : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20'}`} data-id="element-159">
                        <service.icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-slate-400'}`} data-id="element-160" />
                        <span className="text-[9px] font-bold uppercase tracking-wider" data-id="element-161">
                          {service.id}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-162">
                <div className="flex-1" data-id="element-163">
                  <div className="text-[10px] font-bold text-[#22A39F] uppercase tracking-widest mb-1" data-id="element-164">
                    Estimated Annual Savings
                  </div>
                  <div className="text-3xl md:text-4xl font-black text-white flex items-center" data-id="element-165">
                    $<motion.span data-id="element-166">{rounded}</motion.span>
                  </div>
                </div>
                <button onClick={onOpenQuote} className="hidden sm:flex bg-[#22A39F] hover:bg-[#1d8a87] text-white px-6 py-3 rounded-xl font-bold text-base transition-all shadow-lg shadow-[#22A39F]/20 items-center gap-2" data-id="element-167">
                  Get My Free Audit
                </button>
              </div>
            </div>

            {/* Value Messaging Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-168">
              {[{
              icon: TrendingDownIcon,
              label: 'Save up to 30%',
              color: 'text-emerald-400'
            }, {
              icon: ClockIcon,
              label: 'Faster Setup',
              color: 'text-blue-400'
            }, {
              icon: FileXIcon,
              label: 'No Contracts',
              color: 'text-orange-400'
            }, {
              icon: ZapIcon,
              label: 'More Profit',
              color: 'text-yellow-400'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + i * 0.1
            }} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group" data-id="element-169">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 group-hover:scale-110 transition-transform ${item.color}`} data-id="element-170">
                    <item.icon className="w-4 h-4" data-id="element-171" />
                  </div>
                  <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider text-center" data-id="element-172">
                    {item.label}
                  </span>
                </motion.div>)}
            </div>

            <button onClick={onOpenQuote} className="sm:hidden w-full mt-6 bg-[#22A39F] hover:bg-[#1d8a87] text-white px-6 py-3 rounded-xl font-bold text-base transition-all shadow-lg shadow-[#22A39F]/20 flex justify-center items-center gap-2" data-id="element-173">
              Get My Free Audit
            </button>
          </motion.div>

          {/* Right Side: Marketing Animation */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-174">
            <div className="bg-white rounded-[2.5rem] p-4 md:p-6 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden flex flex-col" data-id="element-175">
              <div className="relative w-full rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100" data-id="element-176">
                <video src="https://cdn.magicpatterns.com/uploads/duptjbkSHBLY2UaxyjkrGD/For Website use.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-177" />
              </div>
            </div>

            {/* Floating Trust Badge */}
            <div className="absolute -bottom-6 -right-6 bg-[#22A39F] text-white p-6 rounded-3xl shadow-xl hidden md:block" data-id="element-178">
              <div className="flex items-center gap-4" data-id="element-179">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-180">
                  <ShieldCheckIcon className="w-6 h-6" data-id="element-181" />
                </div>
                <div data-id="element-182">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-183">
                    Verified Savings
                  </div>
                  <div className="text-xl font-bold" data-id="element-184">$2.4M+ Recovered</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}